import { AnimatePresence, motion } from "framer-motion";
import { FC, RefObject } from "react";
import ReactFocusLock from "react-focus-lock";

import { NavDropDownCloseIcon } from "@theme/assets";
import {
  Box,
  Container,
  FeaturedImageModule,
  IconButton,
  NavProductList,
  NavShopAllLink,
  Paragraph,
} from "@theme/components";
import { NavCategory } from "@theme/types";

const TOTAL_NUMBER_OF_COLUMNS = 4;

interface NavDesktopCategoryDropdownProps {
  navCategory: NavCategory;
  closeNavigation: () => void;
  buttonRef: RefObject<HTMLButtonElement>;
}

export const NavDesktopCategoryDropdown: FC<NavDesktopCategoryDropdownProps> = ({
  navCategory,
  closeNavigation,
  buttonRef,
}) => {
  const columns = navCategory.navColumns;

  const filledColumnCount = columns.length;
  const emptyColumnCount = TOTAL_NUMBER_OF_COLUMNS - filledColumnCount;

  return (
    <AnimatePresence>
      <ReactFocusLock
        returnFocus={() => {
          buttonRef.current?.focus();
          return false;
        }}
      >
        <motion.div
          initial="hide"
          animate="show"
          exit="hide"
          variants={{
            hide: { opacity: 0 },
            show: { opacity: 1 },
          }}
          sx={{
            width: "full",
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: -999,
            bg: "beige100",
            overflow: "auto",
            maxHeight: "80vh",
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              closeNavigation();
            }
          }}
          tabIndex={-1}
        >
          <IconButton
            data-testid="close-modal-button"
            aria-label={`Close ${navCategory.title} Product Dropdown`}
            sx={{
              position: "absolute",
              top: "s100",
              right: "s100",
              padding: 0,
              ":hover": {
                backgroundColor: "transparent",
              },
            }}
            onClick={() => {
              closeNavigation();
            }}
            tabIndex={0}
          >
            <NavDropDownCloseIcon sx={{ color: "goldPrimary" }} />
          </IconButton>
          <Container variant="wide">
            <NavShopAllLink
              to={`${navCategory.pageSlug}`}
              onClick={closeNavigation}
              text={navCategory.shopAllCTAText}
              ariaLabel={navCategory.shopAllCTAText}
              sx={{ mt: "s500", width: "fit-content" }}
            />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: "s700",
                pt: "s700",
                pb: "s700",
                width: "100%",
                alignItems: "flex-start",
                alignSelf: "stretch",
              }}
              variant="wide"
            >
              {columns.map(
                (column, columnIndex) =>
                  column.items.length > 0 && (
                    <Box key={columnIndex}>
                      {column.items.map((subCategory) => (
                        <Box
                          as="section"
                          aria-label={subCategory?.title}
                          key={subCategory.id}
                          sx={{ mb: "s700" }}
                        >
                          <Paragraph
                            variant="eyebrow01"
                            sx={{
                              color: "goldDark",
                              textTransform: "uppercase",
                              fontSize: ["fs10", null, "fs10"],
                              mb: "s200",
                            }}
                          >
                            {subCategory?.title}
                          </Paragraph>

                          <NavProductList
                            navCategory={navCategory}
                            subCategory={subCategory}
                            closeNavigation={closeNavigation}
                          />
                        </Box>
                      ))}
                    </Box>
                  ),
              )}
              {navCategory.featuredImageModule && (
                <FeaturedImageModule
                  {...navCategory.featuredImageModule}
                  emptyColumnCount={emptyColumnCount}
                  closeNavigation={closeNavigation}
                />
              )}
            </Box>
          </Container>
        </motion.div>
      </ReactFocusLock>
    </AnimatePresence>
  );
};
